import type { TutorialFile, TutorialVersion } from '@platform/shared-types';
import type { LibraryItem, MeterRow } from '../../services/videoTasksApi';

// Pure maths behind the Library tab (SA-Library, Board 4). No React here so
// the grouping and meter totals are unit-tested on their own.

/** 42 KB · 512 MB · 1.9 GB · 0 B */
export function formatBytes(n: number): string {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v >= 10 || i === 0 ? Math.round(v) : Math.round(v * 10) / 10} ${u[i]}`;
}

export interface LibraryGroup { key: string; label: string; items: LibraryItem[]; bytes: number }

/**
 * Groups by who RECORDED the video (`recordedBy.tenantId`), not who owns it.
 * Items with no recorder are RubyTill-authored and land in 'GLOBAL', which is
 * always last; the rest are largest first.
 */
export function groupByRecorderTenant(items: LibraryItem[]): LibraryGroup[] {
  const m = new Map<string, LibraryItem[]>();
  for (const it of items) {
    const k = it.tutorial.recordedBy?.tenantId ?? 'GLOBAL';
    m.set(k, [...(m.get(k) ?? []), it]);
  }
  const groups = [...m.entries()].map(([key, list]) => ({
    key,
    label: key === 'GLOBAL' ? 'RubyTill global' : key,
    items: list,
    bytes: list.reduce((n, x) => n + x.totalBytes, 0),
  }));
  return [
    ...groups.filter((g) => g.key !== 'GLOBAL').sort((a, b) => b.bytes - a.bytes),
    ...groups.filter((g) => g.key === 'GLOBAL'),
  ];
}

/** Files still on S3 -- a purged file carries `deletedAt`. */
export const liveFiles = (v: Pick<TutorialVersion, 'files'>): TutorialFile[] =>
  (v.files ?? []).filter((f) => !f.deletedAt);

/**
 * Stat-tile totals from the storage meter rows.
 * - used = every OWNER partition's TOTAL (`STORAGE#GLOBAL`, `STORAGE#TENANT#*`).
 *   `STORAGE#RECORDER#*` rows mirror the same bytes, so they are excluded.
 * - recorderTenants = recorder rows still holding bytes.
 * - addedThisMonth = owner `MONTH#YYYY-MM` rows for the current UTC month --
 *   the same `toISOString().slice(0, 7)` the backend writes.
 */
export function storageStats(meters: MeterRow[], now = Date.now()) {
  const month = `MONTH#${new Date(now).toISOString().slice(0, 7)}`;
  const isOwner = (pk: string) => pk === 'STORAGE#GLOBAL' || pk.startsWith('STORAGE#TENANT#');
  let usedBytes = 0;
  let recorderTenants = 0;
  let addedThisMonth = 0;
  for (const r of meters) {
    const bytes = Number(r.bytes) || 0;
    if (isOwner(r.pk) && r.sk === 'TOTAL') usedBytes += bytes;
    if (isOwner(r.pk) && r.sk === month) addedThisMonth += bytes;
    if (r.pk.startsWith('STORAGE#RECORDER#') && r.sk === 'TOTAL' && bytes > 0) recorderTenants++;
  }
  return { usedBytes, recorderTenants, addedThisMonth };
}
